import { useState } from 'react'
import {
  useAdminPackages,
  useCreatePackage,
  useUpdatePackage,
  useDeletePackage,
  formatLimit,
  formatPrice,
  type Package,
  type CreatePackageData,
  type UpdatePackageData,
} from '@/hooks/usePackages'
import { useAuthStore } from '@/stores/authStore'
import {
  Package as PackageIcon,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Star,
  Check,
  X,
  ChevronUp,
  ChevronDown,
  Crown,
  Zap,
  Building2,
  Sparkles,
} from 'lucide-react'
import { toast } from 'sonner'

const PACKAGE_ICONS: Record<string, React.ReactNode> = {
  free: <Sparkles className="h-5 w-5" />,
  starter: <Zap className="h-5 w-5" />,
  pro: <Crown className="h-5 w-5" />,
  enterprise: <Building2 className="h-5 w-5" />,
}

function getPackageIcon(slug: string): React.ReactNode {
  return PACKAGE_ICONS[slug] || <PackageIcon className="h-5 w-5" />
}

interface PackageFormData {
  name: string
  slug: string
  description: string
  price_monthly: string
  price_yearly: string
  is_free: boolean
  is_active: boolean
  is_default: boolean
  sort_order: string
  notifications_per_month: string
  live_sessions_per_month: string
  api_calls_per_month: string
  max_applications: string
  max_team_members: string
  max_api_keys: string
  features: string
  stripe_price_id_monthly: string
  stripe_price_id_yearly: string
}

const initialFormData: PackageFormData = {
  name: '',
  slug: '',
  description: '',
  price_monthly: '0',
  price_yearly: '0',
  is_free: false,
  is_active: true,
  is_default: false,
  sort_order: '0',
  notifications_per_month: '1000',
  live_sessions_per_month: '100',
  api_calls_per_month: '10000',
  max_applications: '1',
  max_team_members: '1',
  max_api_keys: '2',
  features: '',
  stripe_price_id_monthly: '',
  stripe_price_id_yearly: '',
}

function packageToFormData(pkg: Package): PackageFormData {
  return {
    name: pkg.name,
    slug: pkg.slug,
    description: pkg.description || '',
    price_monthly: String(pkg.price_monthly),
    price_yearly: String(pkg.price_yearly),
    is_free: pkg.is_free,
    is_active: pkg.is_active,
    is_default: pkg.is_default,
    sort_order: String(pkg.sort_order),
    notifications_per_month: String(pkg.notifications_per_month),
    live_sessions_per_month: String(pkg.live_sessions_per_month),
    api_calls_per_month: String(pkg.api_calls_per_month),
    max_applications: String(pkg.max_applications),
    max_team_members: String(pkg.max_team_members),
    max_api_keys: String(pkg.max_api_keys),
    features: pkg.features.join('\n'),
    stripe_price_id_monthly: pkg.stripe_price_id_monthly || '',
    stripe_price_id_yearly: pkg.stripe_price_id_yearly || '',
  }
}

function formDataToCreateData(form: PackageFormData): CreatePackageData {
  return {
    name: form.name,
    slug: form.slug,
    description: form.description || undefined,
    price_monthly: parseInt(form.price_monthly) || 0,
    price_yearly: parseInt(form.price_yearly) || 0,
    is_free: form.is_free,
    is_active: form.is_active,
    is_default: form.is_default,
    sort_order: parseInt(form.sort_order) || 0,
    notifications_per_month: parseInt(form.notifications_per_month) || 0,
    live_sessions_per_month: parseInt(form.live_sessions_per_month) || 0,
    api_calls_per_month: parseInt(form.api_calls_per_month) || 0,
    max_applications: parseInt(form.max_applications) || 1,
    max_team_members: parseInt(form.max_team_members) || 1,
    max_api_keys: parseInt(form.max_api_keys) || 1,
    features: form.features.split('\n').filter((f) => f.trim()),
    stripe_price_id_monthly: form.stripe_price_id_monthly || undefined,
    stripe_price_id_yearly: form.stripe_price_id_yearly || undefined,
  }
}

function PackageFormModal({
  isOpen,
  onClose,
  editingPackage,
  onSubmit,
  isSubmitting,
}: {
  isOpen: boolean
  onClose: () => void
  editingPackage: Package | null
  onSubmit: (data: PackageFormData) => void
  isSubmitting: boolean
}) {
  const [formData, setFormData] = useState<PackageFormData>(
    editingPackage ? packageToFormData(editingPackage) : initialFormData
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {editingPackage ? 'Edit Package' : 'Create Package'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Pro"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Slug *</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  placeholder="pro"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={2}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                placeholder="For growing businesses"
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">Pricing (in cents)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Monthly Price (cents)</label>
                <input
                  type="number"
                  name="price_monthly"
                  value={formData.price_monthly}
                  onChange={handleChange}
                  min="0"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Display: {formatPrice(parseInt(formData.price_monthly) || 0)}
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Yearly Price (cents)</label>
                <input
                  type="number"
                  name="price_yearly"
                  value={formData.price_yearly}
                  onChange={handleChange}
                  min="0"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Display: {formatPrice(parseInt(formData.price_yearly) || 0)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Stripe Monthly Price ID</label>
                <input
                  type="text"
                  name="stripe_price_id_monthly"
                  value={formData.stripe_price_id_monthly}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  placeholder="price_xxx"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Stripe Yearly Price ID</label>
                <input
                  type="text"
                  name="stripe_price_id_yearly"
                  value={formData.stripe_price_id_yearly}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  placeholder="price_xxx"
                />
              </div>
            </div>
          </div>

          {/* Limits */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">
              Limits (-1 = Unlimited)
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Notifications/mo</label>
                <input
                  type="number"
                  name="notifications_per_month"
                  value={formData.notifications_per_month}
                  onChange={handleChange}
                  min="-1"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Live Sessions/mo</label>
                <input
                  type="number"
                  name="live_sessions_per_month"
                  value={formData.live_sessions_per_month}
                  onChange={handleChange}
                  min="-1"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">API Calls/mo</label>
                <input
                  type="number"
                  name="api_calls_per_month"
                  value={formData.api_calls_per_month}
                  onChange={handleChange}
                  min="-1"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Max Applications</label>
                <input
                  type="number"
                  name="max_applications"
                  value={formData.max_applications}
                  onChange={handleChange}
                  min="-1"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Max Team Members</label>
                <input
                  type="number"
                  name="max_team_members"
                  value={formData.max_team_members}
                  onChange={handleChange}
                  min="-1"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Max API Keys</label>
                <input
                  type="number"
                  name="max_api_keys"
                  value={formData.max_api_keys}
                  onChange={handleChange}
                  min="-1"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">
              Additional Features (one per line)
            </h3>
            <textarea
              name="features"
              value={formData.features}
              onChange={handleChange}
              rows={4}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              placeholder="Priority support&#10;Custom branding&#10;Webhooks"
            />
          </div>

          {/* Flags */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground">Options</h3>
            <div className="grid grid-cols-3 gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_free"
                  checked={formData.is_free}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-input"
                />
                <span className="text-sm">Free Plan</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-input"
                />
                <span className="text-sm">Active</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_default"
                  checked={formData.is_default}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-input"
                />
                <span className="text-sm">Default</span>
              </label>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Sort Order</label>
              <input
                type="number"
                name="sort_order"
                value={formData.sort_order}
                onChange={handleChange}
                min="0"
                className="w-full max-w-[120px] rounded-lg border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-input hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingPackage ? 'Update Package' : 'Create Package'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  packageName,
  isDeleting,
}: {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  packageName: string
  isDeleting: boolean
}) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-2">Delete Package</h2>
        <p className="text-muted-foreground mb-4">
          Are you sure you want to delete <strong>{packageName}</strong>? This action cannot be
          undone.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-input hover:bg-accent"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
          >
            {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

export function PackagesPage() {
  const { user } = useAuthStore()
  const { data, isLoading, error } = useAdminPackages()
  const createMutation = useCreatePackage()
  const updateMutation = useUpdatePackage()
  const deleteMutation = useDeletePackage()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingPackage, setEditingPackage] = useState<Package | null>(null)
  const [deletePackage, setDeletePackage] = useState<Package | null>(null)

  const isSuperAdmin = user?.role === 'super_admin'

  const handleCreate = () => {
    setEditingPackage(null)
    setIsFormOpen(true)
  }

  const handleEdit = (pkg: Package) => {
    setEditingPackage(pkg)
    setIsFormOpen(true)
  }

  const handleFormSubmit = async (formData: PackageFormData) => {
    try {
      if (editingPackage) {
        await updateMutation.mutateAsync({
          id: editingPackage.id,
          data: formDataToCreateData(formData) as UpdatePackageData,
        })
        toast.success('Package updated successfully')
      } else {
        await createMutation.mutateAsync(formDataToCreateData(formData))
        toast.success('Package created successfully')
      }
      setIsFormOpen(false)
      setEditingPackage(null)
    } catch (err) {
      toast.error(editingPackage ? 'Failed to update package' : 'Failed to create package')
    }
  }

  const handleDelete = async () => {
    if (!deletePackage) return
    try {
      await deleteMutation.mutateAsync(deletePackage.id)
      toast.success('Package deleted successfully')
      setDeletePackage(null)
    } catch (err) {
      toast.error('Failed to delete package')
    }
  }

  const handleToggleActive = async (pkg: Package) => {
    try {
      await updateMutation.mutateAsync({
        id: pkg.id,
        data: { is_active: !pkg.is_active },
      })
      toast.success(`Package ${pkg.is_active ? 'deactivated' : 'activated'}`)
    } catch (err) {
      toast.error('Failed to update package')
    }
  }

  const handleSetDefault = async (pkg: Package) => {
    try {
      await updateMutation.mutateAsync({
        id: pkg.id,
        data: { is_default: true },
      })
      toast.success(`${pkg.name} is now the default package`)
    } catch (err) {
      toast.error('Failed to set default package')
    }
  }

  const packages = data?.packages || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/20 text-destructive-foreground rounded-lg p-4 text-sm">
        Failed to load packages. Please try again.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Packages</h1>
          <p className="text-muted-foreground">Manage subscription packages and pricing</p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Create Package
          </button>
        )}
      </div>

      {!isSuperAdmin && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 rounded-lg p-4 text-sm">
          You need Super Admin privileges to modify packages.
        </div>
      )}

      {/* Packages Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Package
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Pricing
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Limits
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {packages
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((pkg) => (
                  <tr key={pkg.id} className={!pkg.is_active ? 'opacity-50' : undefined}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="text-primary">{getPackageIcon(pkg.slug)}</div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{pkg.name}</span>
                            {pkg.is_default && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">
                                <Star className="h-3 w-3" />
                                Default
                              </span>
                            )}
                            {pkg.is_free && (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/10 text-green-500">
                                Free
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{pkg.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <p className="font-medium">
                          {formatPrice(pkg.price_monthly)}
                          <span className="text-muted-foreground font-normal">/mo</span>
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {formatPrice(pkg.price_yearly)}/yr
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-xs space-y-1">
                        <p>
                          <span className="text-muted-foreground">Notifications:</span>{' '}
                          {formatLimit(pkg.notifications_per_month)}
                        </p>
                        <p>
                          <span className="text-muted-foreground">Live Sessions:</span>{' '}
                          {formatLimit(pkg.live_sessions_per_month)}
                        </p>
                        <p>
                          <span className="text-muted-foreground">API Calls:</span>{' '}
                          {formatLimit(pkg.api_calls_per_month)}
                        </p>
                        <p>
                          <span className="text-muted-foreground">Apps:</span>{' '}
                          {formatLimit(pkg.max_applications)}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => isSuperAdmin && handleToggleActive(pkg)}
                        disabled={!isSuperAdmin}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                          pkg.is_active
                            ? 'bg-green-500/10 text-green-500'
                            : 'bg-red-500/10 text-red-500'
                        } ${isSuperAdmin ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                      >
                        {pkg.is_active ? (
                          <>
                            <Check className="h-3 w-3" />
                            Active
                          </>
                        ) : (
                          <>
                            <X className="h-3 w-3" />
                            Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {isSuperAdmin && !pkg.is_default && (
                          <button
                            onClick={() => handleSetDefault(pkg)}
                            title="Set as default"
                            className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg"
                          >
                            <Star className="h-4 w-4" />
                          </button>
                        )}
                        {isSuperAdmin && (
                          <>
                            <button
                              onClick={() => handleEdit(pkg)}
                              title="Edit package"
                              className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeletePackage(pkg)}
                              title="Delete package"
                              className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {packages.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            <PackageIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No packages found</p>
            {isSuperAdmin && (
              <button
                onClick={handleCreate}
                className="mt-4 text-sm text-primary hover:underline"
              >
                Create your first package
              </button>
            )}
          </div>
        )}
      </div>

      {/* Form Modal */}
      <PackageFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setEditingPackage(null)
        }}
        editingPackage={editingPackage}
        onSubmit={handleFormSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deletePackage}
        onClose={() => setDeletePackage(null)}
        onConfirm={handleDelete}
        packageName={deletePackage?.name || ''}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  )
}
